import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, PlayCircle, Camera, Compass, Trees, Target, Notebook, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { NewsletterForm } from '@/components/site/NewsletterForm';
import { SeasonCountdown } from '@/components/site/SeasonCountdown';
import { absoluteImageUrl } from '@/components/admin/ImageUploader';
import { publicApi } from '@/lib/api';
import { youtubeEmbedUrl, formatDate, labelFor, FIELD_NOTE_CATEGORIES } from '@/lib/taxonomies';
import { useSeo } from '@/lib/seo';
import { HERO, SEASON } from '@/constants/testIds';

const TIMELINE = [
  { key: 'preseason', label: 'Preseason', Icon: Trees, description: 'Reading maps. Walking edges before the leaves fall.' },
  { key: 'trail_cameras', label: 'Trail Cameras', Icon: Camera, description: 'Cards pulled, angles re-thought, patience tested.' },
  { key: 'public_land', label: 'Public-Land Scouting', Icon: Compass, description: 'Miles of boot prints in pressured ground.' },
  { key: 'small_property', label: 'Small-Property Observations', Icon: Notebook, description: '1.25 acres of paying close attention.' },
  { key: 'hunts', label: 'Hunts', Icon: Target, description: 'The sits that actually happened.' },
  { key: 'lessons', label: 'Lessons Learned', Icon: Notebook, description: 'What worked. What did not. Both count.' },
  { key: 'results', label: 'Season Outcome', Icon: Trophy, description: 'Honest results, whatever they are.' },
];

const HERO_IMAGE = 'https://images.unsplash.com/flagged/photo-1552863474-9e7df7c5fc47?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85';

export default function HomePage() {
  const [video, setVideo] = useState(null);
  const [notes, setNotes] = useState([]);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [autoThumb, setAutoThumb] = useState(null);
  const [openers, setOpeners] = useState([]);

  useSeo({
    title: 'A Guy Who Hunts | Public Land, Limited Time, Honest Results',
    description:
      'Follow a self-taught adult hunter documenting public-land scouting, small-property whitetail hunting, mistakes, lessons, and the pursuit of his first deer.',
    ogImage: HERO_IMAGE,
  });

  useEffect(() => {
    publicApi.featuredVideo().then(setVideo).catch(() => {});
    publicApi.fieldNotes({ limit: 3 }).then(setNotes).catch(() => setNotes([]));
    // Try to fetch (or reuse cached) YouTube thumbnail — only used when no
    // custom cover_image is set. Failures are silent; we just show the iframe
    // (or a branded neutral fallback if neither is available).
    publicApi.featuredVideoThumbnail().then(setAutoThumb).catch(() => setAutoThumb(null));
    publicApi
      .siteConfig()
      .then((cfg) => setOpeners(Array.isArray(cfg?.openers) ? cfg.openers : []))
      .catch(() => setOpeners([]));
  }, []);

  const embed = video?.youtube_url ? youtubeEmbedUrl(video.youtube_url) : null;
  const cover =
    (video?.cover_image?.url && absoluteImageUrl(video.cover_image.url)) ||
    (autoThumb?.url ? absoluteImageUrl(autoThumb.url) : null);
  const canAutoplay = embed ? `${embed}${embed.includes('?') ? '&' : '?'}autoplay=1` : null;

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden isolate">
        <div className="absolute inset-0 z-0">
          <img
            src={HERO_IMAGE}
            alt="Cinematic forest path in soft morning light — placeholder image."
            className="h-full w-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/40 to-[hsl(var(--background))]" />
          <div className="noise-overlay" />
        </div>
        <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-24 sm:pt-32 lg:pt-40 pb-16 sm:pb-24">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="max-w-3xl"
          >
            <p className="kicker text-[#F6F2E8]/85 mb-4">A hunting documentary in progress</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[#F6F2E8]">
              Public land. Limited time. <span className="italic">Honest results.</span>
            </h1>
            <p className="mt-5 max-w-2xl text-base sm:text-lg text-[#F6F2E8]/90 leading-relaxed">
              Follow a self-taught adult hunter trying to harvest his first whitetail while balancing work, family,
              pressured public land, and a 1.25-acre hunting property.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/season">
                <Button size="lg" className="h-12 px-6" data-testid={HERO.primaryCta}>
                  Follow the Season
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <a
                href={video?.youtube_url || 'https://www.youtube.com/'}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={HERO.secondaryCta}
              >
                <Button size="lg" variant="secondary" className="h-12 px-6">
                  <PlayCircle size={18} className="mr-2" />
                  Watch on YouTube
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Latest Video */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14 lg:py-18">
        <div className="mb-10">
          <SeasonCountdown openers={openers} />
        </div>
        <div className="grid gap-8 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-7">
            <Card className="card-elev overflow-hidden border-[hsl(var(--border))]">
              <AspectRatio ratio={16 / 9}>
                {cover && !videoPlaying ? (
                  <button
                    type="button"
                    onClick={() => embed && setVideoPlaying(true)}
                    disabled={!embed}
                    aria-label={embed ? `Play ${video?.title || 'featured video'}` : 'Featured video'}
                    data-testid="home-featured-video-cover"
                    className="group relative h-full w-full overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
                  >
                    <img
                      src={cover}
                      alt={video?.cover_image?.alt || video?.title || 'Featured video cover'}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-white/92 flex items-center justify-center shadow-lg transition-transform group-hover:scale-105">
                        <PlayCircle size={44} className="text-[hsl(var(--primary))]" strokeWidth={1.5} />
                      </div>
                    </div>
                    {embed && (
                      <span className="absolute bottom-3 right-3 rounded-full bg-black/60 text-white text-[11px] px-2 py-0.5">
                        Watch trailer
                      </span>
                    )}
                  </button>
                ) : canAutoplay || embed ? (
                  <iframe
                    src={videoPlaying && canAutoplay ? canAutoplay : embed}
                    title={video?.title || 'Latest video'}
                    className="h-full w-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center bg-gradient-to-br from-[hsl(var(--forest-900,150_24%_15%))] to-[hsl(var(--forest-800,150_24%_20%))] text-[hsl(var(--paper-50,40_33%_94%))]">
                    <div className="text-center px-4">
                      <p className="kicker text-[#F6F2E8]/70 mb-2">Latest video</p>
                      <p className="font-serif text-2xl leading-tight">The next episode is being edited.</p>
                      <p className="mt-2 text-sm text-[#F6F2E8]/80">Subscribe to The Field Report to know when it drops.</p>
                    </div>
                  </div>
                )}
              </AspectRatio>
            </Card>
          </div>
          <div className="lg:col-span-5">
            <p className="kicker">Latest video</p>
            <h2 className="font-serif text-2xl sm:text-3xl leading-tight mt-2 tracking-tight">
              {video?.title || 'A new episode is on the way.'}
            </h2>
            <p className="mt-3 text-[hsl(var(--muted-foreground))] leading-relaxed">
              {video?.description ||
                'The next episode is being edited. Subscribe to The Field Report to know when it drops.'}
            </p>
            {video?.youtube_url && (
              <a href={video.youtube_url} target="_blank" rel="noopener noreferrer">
                <Button variant="secondary" className="mt-5">
                  Watch on YouTube <ArrowRight size={16} className="ml-2" />
                </Button>
              </a>
            )}
          </div>
        </div>
      </section>

      {/* The Story */}
      <section className="bg-[hsl(var(--secondary)/0.55)] border-y border-[hsl(var(--border))]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <div className="grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <p className="kicker">The story</p>
              <h2 className="font-serif text-3xl sm:text-4xl mt-2 tracking-tight">
                Not an expert. Just an honest process.
              </h2>
            </div>
            <div className="lg:col-span-7 lg:col-start-6 prose-editorial">
              <p>
                I did not grow up hunting. I started as an adult and have mostly learned through research, observation,
                trial, error, and time in the woods.
              </p>
              <p>
                I am not making this because I have everything figured out. I am making it because I do not.
              </p>
              <p>
                This site documents the real process of trying to become a better whitetail hunter while balancing work,
                family, limited time, pressured public land, and the small pieces of ground I can actually access —
                including about 1.25 acres behind the house.
              </p>
              <p className="text-[hsl(var(--muted-foreground))]">
                I have not harvested my first whitetail yet. That is the story.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Follow the Season timeline */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="kicker">Follow the season</p>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mt-2">The season, in stages.</h2>
            <p className="mt-2 max-w-2xl text-[hsl(var(--muted-foreground))]">
              A documentary timeline of everything that happens between the first camera going up and the last light on
              the last sit.
            </p>
          </div>
          <Link to="/season" className="hidden sm:inline-flex">
            <Button variant="ghost" data-testid={SEASON.filterAll}>
              View all <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>

        <ol className="relative mt-10 pl-6 before:absolute before:left-2 before:top-2 before:bottom-2 before:w-px before:bg-[hsl(var(--border))]">
          {TIMELINE.map(({ key, label, description, Icon }) => (
            <li key={key} className="relative pb-8 last:pb-0">
              <span className="absolute -left-[1.35rem] top-1 flex h-5 w-5 items-center justify-center rounded-full bg-[hsl(var(--primary))] ring-4 ring-[hsl(var(--background))]">
                <Icon size={11} className="text-[hsl(var(--primary-foreground))]" />
              </span>
              <Link
                to={`/season?type=${key}`}
                className="group inline-flex flex-col rounded-xl px-3 -mx-3 py-2 transition-colors hover:bg-[hsl(var(--accent)/0.25)]"
              >
                <span className="kicker">{label}</span>
                <span className="font-serif text-lg sm:text-xl text-[hsl(var(--foreground))] mt-1">
                  {description}
                </span>
              </Link>
            </li>
          ))}
        </ol>
      </section>

      {/* Recent Field Notes */}
      <section className="bg-[hsl(var(--secondary)/0.55)] border-y border-[hsl(var(--border))]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="kicker">Field notes</p>
              <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mt-2">Recent writing.</h2>
            </div>
            <Link to="/field-notes" className="hidden sm:inline-flex">
              <Button variant="ghost">
                All notes <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </div>
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {notes.length === 0 && (
              <p className="text-sm text-[hsl(var(--muted-foreground))]">No notes published yet.</p>
            )}
            {notes.slice(0, 3).map((n) => (
              <Link key={n.slug} to={`/field-notes/${n.slug}`} className="group">
                <Card className="h-full overflow-hidden border-[hsl(var(--border))] card-elev card-elev-hover transition-shadow">
                  <AspectRatio ratio={4 / 3}>
                    <img
                      src={absoluteImageUrl(n.hero_image?.url)}
                      alt={n.hero_image?.alt || n.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                      loading="lazy"
                    />
                  </AspectRatio>
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
                      <Badge variant="secondary" className="font-normal">
                        {labelFor(FIELD_NOTE_CATEGORIES, n.category)}
                      </Badge>
                      <span>•</span>
                      <time>{formatDate(n.date)}</time>
                    </div>
                    <h3 className="font-serif text-xl leading-snug mt-2 tracking-tight">
                      {n.title}
                    </h3>
                    <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed line-clamp-3">
                      {n.excerpt}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24 text-center">
        <p className="kicker">The Field Report</p>
        <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mt-2">Get the field report.</h2>
        <p className="mt-3 text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto">
          New videos, trail-camera updates, scouting notes, mistakes, and honest results — sent when there is something
          worth sharing.
        </p>
        <div className="mt-8 max-w-xl mx-auto text-left">
          <NewsletterForm variant="full" />
        </div>
      </section>
    </div>
  );
}
